import { noteRoots, getNoteMeta, putNoteMeta, deleteNoteMeta, withBlockIds } from './chunk-7U33VFN2.js';
import { yElementToPM, pmNodeToY, yFragmentToPM } from './chunk-TCGBD5LJ.js';
import { rewrapNoteKey } from './chunk-PXX4LS7M.js';
import { pmNodeSchema, BLOCK_NODES, INLINE_NODES, MARKS, TEXTBLOCK_NODES, TASK_STATES } from './chunk-PP2CXEGZ.js';
import { uuidv7 } from './chunk-RPUENDSP.js';
import * as Y from 'yjs';
import { z } from 'zod';

function findBlock(doc, blockId) {
  const visit = (parent) => {
    const children2 = parent.toArray();
    for (let i = 0; i < children2.length; i++) {
      const child = children2[i];
      if (!(child instanceof Y.XmlElement)) continue;
      if (child.getAttribute("blockId") === blockId) return { element: child, parent, index: i };
      const found = visit(child);
      if (found) return found;
    }
    return null;
  };
  return visit(noteRoots(doc).content);
}
function getBlock(doc, blockId) {
  const loc = findBlock(doc, blockId);
  return loc ? yElementToPM(loc.element) : null;
}
function listTopLevelBlocks(doc) {
  return noteRoots(doc).content.toArray().filter((c) => c instanceof Y.XmlElement && c.nodeName !== "title").map(yElementToPM);
}
var positionSchema = z.union([
  z.object({ after: z.string() }),
  z.object({ before: z.string() }),
  /** Inside a container block (list, quote, callout, list/task item…); omitted = top level. */
  z.object({ parent: z.string().nullable().optional(), at: z.enum(["start", "end"]) })
]);
var blockOperationSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("insert"),
    position: positionSchema,
    blocks: z.array(pmNodeSchema).min(1)
  }),
  z.object({ op: z.literal("replace"), blockId: z.string(), block: pmNodeSchema }),
  z.object({ op: z.literal("delete"), blockId: z.string() }),
  z.object({
    op: z.literal("updateAttrs"),
    blockId: z.string(),
    attrs: z.record(z.string(), z.unknown())
  }),
  z.object({ op: z.literal("setTitle"), text: z.string() })
]);
var BlockOperationError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
  }
  code;
  name = "BlockOperationError";
};
var KNOWN_BLOCKS = new Set(BLOCK_NODES);
var KNOWN_INLINE = new Set(INLINE_NODES);
var KNOWN_MARKS = new Set(MARKS);
var TEXTBLOCKS = new Set(TEXTBLOCK_NODES);
function validateBlock(node, path = "block") {
  const fail = (msg) => {
    throw new BlockOperationError("invalid_block", `${path}: ${msg}`);
  };
  if (!KNOWN_BLOCKS.has(node.type)) fail(`unknown block type "${node.type}"`);
  if (node.type === "title") fail("title cannot be inserted");
  if (TEXTBLOCKS.has(node.type)) {
    (node.content ?? []).forEach((c, i) => {
      const p = `${path}.content[${i}]`;
      if (c.type === "text") {
        if (typeof c.text !== "string" || c.text === "")
          throw new BlockOperationError("invalid_block", `${p}: empty text`);
        if (node.type === "codeBlock" && c.marks?.length) {
          throw new BlockOperationError("invalid_block", `${p}: code blocks have no marks`);
        }
        for (const m of c.marks ?? []) {
          if (!KNOWN_MARKS.has(m.type))
            throw new BlockOperationError("invalid_block", `${p}: unknown mark "${m.type}"`);
        }
      } else if (!KNOWN_INLINE.has(c.type) || node.type === "codeBlock") {
        throw new BlockOperationError(
          "invalid_block",
          `${p}: "${c.type}" is not allowed in ${node.type}`
        );
      }
    });
  } else {
    (node.content ?? []).forEach((c, i) => validateBlock(c, `${path}.content[${i}]`));
  }
}
function addAgentMark(node, mark) {
  if (node.type === "text") {
    return {
      ...node,
      marks: [
        ...(node.marks ?? []).filter((m) => m.type !== "agentEdit"),
        { type: "agentEdit", attrs: mark }
      ]
    };
  }
  return node.content ? { ...node, content: node.content.map((c) => addAgentMark(c, mark)) } : node;
}
function resolvePosition(doc, position) {
  const content = noteRoots(doc).content;
  if ("after" in position || "before" in position) {
    const id = "after" in position ? position.after : position.before;
    const loc = findBlock(doc, id);
    if (!loc) throw new BlockOperationError("not_found", `block ${id} not found`);
    if (loc.element.nodeName === "title" && "before" in position) {
      throw new BlockOperationError("forbidden", "nothing can precede the title");
    }
    return { parent: loc.parent, index: "after" in position ? loc.index + 1 : loc.index };
  }
  let parent = content;
  if (position.parent) {
    const loc = findBlock(doc, position.parent);
    if (!loc) throw new BlockOperationError("not_found", `block ${position.parent} not found`);
    if (TEXTBLOCKS.has(loc.element.nodeName)) {
      throw new BlockOperationError(
        "invalid_block",
        `${loc.element.nodeName} cannot contain blocks`
      );
    }
    parent = loc.element;
  }
  let index = position.at === "start" ? 0 : parent.length;
  if (parent === content && index === 0 && content.length > 0) {
    const first = content.get(0);
    if (first instanceof Y.XmlElement && first.nodeName === "title") index = 1;
  }
  return { parent, index };
}
function prepare(node, options, newId) {
  validateBlock(node);
  let out = withBlockIds(node, newId);
  if (options.agentEdit) out = addAgentMark(out, options.agentEdit);
  return out;
}
function applyBlockOperations(doc, ops, options = {}) {
  const newId = options.newId ?? (() => uuidv7());
  const parsed = ops.map((op) => blockOperationSchema.parse(op));
  for (const op of parsed) {
    if (op.op === "insert") op.blocks.forEach((b) => validateBlock(b));
    if (op.op === "replace") validateBlock(op.block);
  }
  const blockIds = [];
  const probe = new Y.Doc();
  Y.applyUpdate(probe, Y.encodeStateAsUpdate(doc));
  runOps(probe, parsed, options, () => uuidv7());
  doc.transact(() => {
    blockIds.push(...runOps(doc, parsed, options, newId));
  }, options.origin);
  return { blockIds };
}
function runOps(doc, ops, options, newId) {
  const ids = [];
  for (const op of ops) {
    switch (op.op) {
      case "insert": {
        const { parent, index } = resolvePosition(doc, op.position);
        const nodes = op.blocks.map((b) => prepare(b, options, newId));
        parent.insert(index, nodes.map(pmNodeToY));
        ids.push(...nodes.map((n) => n.attrs.blockId));
        break;
      }
      case "replace": {
        const loc = findBlock(doc, op.blockId);
        if (!loc) throw new BlockOperationError("not_found", `block ${op.blockId} not found`);
        if (loc.element.nodeName === "title")
          throw new BlockOperationError("forbidden", "use setTitle for the title");
        const node = prepare(
          { ...op.block, attrs: { ...op.block.attrs ?? {}, blockId: op.blockId } },
          options,
          newId
        );
        loc.parent.delete(loc.index, 1);
        loc.parent.insert(loc.index, [pmNodeToY(node)]);
        ids.push(op.blockId);
        break;
      }
      case "delete": {
        const loc = findBlock(doc, op.blockId);
        if (!loc) throw new BlockOperationError("not_found", `block ${op.blockId} not found`);
        if (loc.element.nodeName === "title")
          throw new BlockOperationError("forbidden", "the title cannot be deleted");
        loc.parent.delete(loc.index, 1);
        break;
      }
      case "updateAttrs": {
        const loc = findBlock(doc, op.blockId);
        if (!loc) throw new BlockOperationError("not_found", `block ${op.blockId} not found`);
        for (const [k, v] of Object.entries(op.attrs)) {
          if (k === "blockId") throw new BlockOperationError("forbidden", "blockId is immutable");
          if (v === null || v === void 0) loc.element.removeAttribute(k);
          else loc.element.setAttribute(k, v);
        }
        break;
      }
      case "setTitle": {
        setTitle(doc, op.text, options);
        break;
      }
    }
  }
  return ids;
}
function setTitle(doc, text, options = {}) {
  const content = noteRoots(doc).content;
  doc.transact(() => {
    let title = content.length > 0 ? content.get(0) : null;
    if (!(title instanceof Y.XmlElement) || title.nodeName !== "title") {
      const el2 = new Y.XmlElement("title");
      el2.setAttribute("blockId", (options.newId ?? (() => uuidv7()))());
      content.insert(0, [el2]);
      title = el2;
    }
    const el = title;
    if (el.length > 0) el.delete(0, el.length);
    if (text) {
      const t = new Y.XmlText();
      t.applyDelta([
        { insert: text, attributes: options.agentEdit ? { agentEdit: options.agentEdit } : {} }
      ]);
      el.insert(0, [t]);
    }
  }, options.origin);
}
var INLINE_TEXT_BLOCKS = /* @__PURE__ */ new Set(["title", "paragraph", "heading", "codeBlock"]);
var ATTR_TEXT_BLOCKS = /* @__PURE__ */ new Set(["mathBlock", "mermaid", "image", "file"]);
function inlineText(el) {
  let out = "";
  for (const child of el.toArray()) {
    if (child instanceof Y.XmlText) out += deltaText(child);
    else if (child.nodeName === "noteLink") out += String(child.getAttribute("label") ?? "");
    else if (child.nodeName === "tag") out += `#${String(child.getAttribute("name") ?? "")}`;
    else if (child.nodeName === "hardBreak") out += "\n";
    else if (child.nodeName === "mention") out += String(child.getAttribute("label") ?? "");
  }
  return out;
}
function deltaText(text) {
  let s = "";
  for (const d of text.toDelta())
    if (typeof d.insert === "string") s += d.insert;
  return s;
}
function attrText(el) {
  switch (el.nodeName) {
    case "mathBlock":
      return String(el.getAttribute("latex") ?? "");
    case "mermaid":
      return String(el.getAttribute("source") ?? "");
    case "image":
      return [el.getAttribute("alt"), el.getAttribute("caption")].filter(Boolean).join(" ");
    case "file":
      return String(el.getAttribute("name") ?? "");
    default:
      return "";
  }
}
function walkElements(doc, visit) {
  const rec = (parent, blockId, depth) => {
    for (const child of parent.toArray()) {
      if (!(child instanceof Y.XmlElement)) continue;
      const own = child.getAttribute("blockId");
      const id = typeof own === "string" ? own : blockId;
      if (visit(child, id, depth) === "skip") continue;
      rec(child, id, depth + 1);
    }
  };
  rec(noteRoots(doc).content, null, 0);
}
function getTitle(doc) {
  const first = noteRoots(doc).content.length > 0 ? noteRoots(doc).content.get(0) : null;
  return first instanceof Y.XmlElement && first.nodeName === "title" ? inlineText(first) : "";
}
function getPlainText(doc) {
  const lines = [];
  walkElements(doc, (el) => {
    const text = INLINE_TEXT_BLOCKS.has(el.nodeName) ? inlineText(el) : ATTR_TEXT_BLOCKS.has(el.nodeName) ? attrText(el) : "";
    if (text) lines.push(text);
  });
  return lines.join("\n");
}
function firstParagraphText(el) {
  for (const child of el.toArray()) {
    if (child instanceof Y.XmlElement && child.nodeName === "paragraph") return inlineText(child);
  }
  return "";
}
function str(v) {
  return typeof v === "string" && v !== "" ? v : null;
}
function extractTasks(doc, noteId) {
  const out = [];
  walkElements(doc, (el) => {
    if (el.nodeName !== "taskItem") return;
    const taskId = str(el.getAttribute("taskId"));
    const blockId = str(el.getAttribute("blockId"));
    if (!taskId || !blockId) return;
    const rawState = el.getAttribute("state");
    const state = TASK_STATES.includes(rawState) ? rawState : "open";
    out.push({
      noteId,
      taskId,
      blockId,
      text: firstParagraphText(el),
      state,
      scheduled: str(el.getAttribute("scheduled")),
      deadline: str(el.getAttribute("deadline")),
      completedAt: str(el.getAttribute("completedAt")),
      order: out.length
    });
  });
  return out;
}
function extractNoteLinks(doc, noteId) {
  const out = [];
  walkElements(doc, (el, blockId) => {
    if (el.nodeName === "noteLink") {
      const to = str(el.getAttribute("noteId"));
      if (to)
        out.push({
          fromNoteId: noteId,
          toNoteId: to,
          blockId,
          label: String(el.getAttribute("label") ?? "")
        });
    } else if (el.nodeName === "pageCard") {
      const to = str(el.getAttribute("noteId"));
      if (to) out.push({ fromNoteId: noteId, toNoteId: to, blockId, label: "" });
    }
  });
  return out;
}
function buildBacklinks(links) {
  const index = /* @__PURE__ */ new Map();
  for (const link of links) {
    const list = index.get(link.toNoteId);
    if (list) list.push(link);
    else index.set(link.toNoteId, [link]);
  }
  return index;
}
function extractTags(doc) {
  const seen = /* @__PURE__ */ new Set();
  walkElements(doc, (el) => {
    if (el.nodeName !== "tag") return;
    const name = str(el.getAttribute("name"));
    if (name && /\p{L}/u.test(name)) seen.add(name);
  });
  return [...seen];
}
function tagAncestors(name) {
  const parts = name.split("/").filter(Boolean);
  return parts.map((_, i) => parts.slice(0, i + 1).join("/"));
}

// src/document/markdown.ts
var CALLOUT_KIND = {
  note: "note",
  info: "info",
  success: "success",
  warning: "warning",
  danger: "danger"
};
function noteToMarkdown(doc, options = {}) {
  return pmDocToMarkdown(yFragmentToPM(noteRoots(doc).content), options);
}
function pmDocToMarkdown(root, options = {}) {
  const ctx = {
    opts: {
      attachmentUrl: (id) => `attachment:${id}`,
      handwritingUrl: (id) => `handwriting:${id}`,
      ...options
    }
  };
  const blocks = (root.content ?? []).map((n) => block(n, ctx)).filter((s) => s !== null);
  return blocks.join("\n\n").replace(/\n{3,}/g, "\n\n").trimEnd() + "\n";
}
function attr(node, key) {
  const v = node.attrs?.[key];
  return v === void 0 || v === null ? null : v;
}
function prefixLines(text, first, rest = first) {
  return text.split("\n").map((line, i) => (i === 0 ? first : rest) + line).join("\n").replace(/[ \t]+$/gm, "");
}
function children(node, ctx, sep = "\n\n") {
  return (node.content ?? []).map((n) => block(n, ctx)).filter((s) => s !== null).join(sep);
}
function withHandwriting(node, md, ctx) {
  const id = attr(node, "blockId");
  if (id && ctx.opts.blocksWithHandwriting?.has(id)) {
    return `${md}

![\u0420\u0443\u043A\u043E\u043F\u0438\u0441\u043D\u0430\u044F \u0437\u0430\u043C\u0435\u0442\u043A\u0430](${ctx.opts.handwritingUrl(id)})`;
  }
  return md;
}
function block(node, ctx) {
  const md = blockBody(node, ctx);
  return md === null ? null : withHandwriting(node, md, ctx);
}
function blockBody(node, ctx) {
  switch (node.type) {
    case "title": {
      const t = inline(node.content, ctx);
      return t ? `# ${t}` : null;
    }
    case "paragraph":
      return escapeBlockStart(inline(node.content, ctx));
    case "heading": {
      const level = Math.min(Math.max(Number(attr(node, "level") ?? 1), 1), 3);
      return `${"#".repeat(level + 1)} ${inline(node.content, ctx)}`;
    }
    case "bulletList":
      return listItems(node, ctx, () => "- ");
    case "orderedList": {
      const start = Number(attr(node, "start") ?? 1);
      return listItems(node, ctx, (i) => `${start + i}. `);
    }
    case "taskList":
      return listItems(node, ctx, (_, item) => {
        const state = attr(item, "state");
        return `- [${state === "done" ? "x" : state === "cancelled" ? "-" : " "}] `;
      });
    case "listItem":
    case "taskItem":
      return children(node, ctx);
    case "blockquote":
      return prefixLines(children(node, ctx), "> ");
    case "callout": {
      const kind = CALLOUT_KIND[attr(node, "tone") ?? "note"] ?? "note";
      const emoji = attr(node, "emoji");
      const head = `[!${kind}]${emoji ? ` ${emoji}` : ""}`;
      const body = children(node, ctx);
      return prefixLines(body ? `${head}
${body}` : head, "> ");
    }
    case "codeBlock": {
      const text = plain(node.content);
      const fence = text.includes("```") ? "````" : "```";
      return `${fence}${attr(node, "language") ?? ""}
${text}
${fence}`;
    }
    case "mathBlock":
      return `$$
${attr(node, "latex") ?? ""}
$$`;
    case "mermaid":
      return "```mermaid\n" + (attr(node, "source") ?? "") + "\n```";
    case "horizontalRule":
      return "---";
    case "table":
      return table(node, ctx);
    case "image": {
      const id = attr(node, "attachmentId");
      const alt = escapeText(attr(node, "alt") ?? "");
      const img = id ? `![${alt}](${ctx.opts.attachmentUrl(id)})` : "";
      const caption = attr(node, "caption");
      return caption ? `${img}
*${escapeText(caption)}*` : img;
    }
    case "file": {
      const id = attr(node, "attachmentId");
      const name = escapeText(attr(node, "name") ?? "file");
      return id ? `[${name}](${ctx.opts.attachmentUrl(id)})` : name;
    }
    case "drawing": {
      const id = attr(node, "blockId");
      return id ? `![\u0420\u0438\u0441\u0443\u043D\u043E\u043A](${ctx.opts.handwritingUrl(id)})` : null;
    }
    case "pageCard": {
      const id = attr(node, "noteId");
      return id ? `[[${ctx.opts.noteTitle?.(id) ?? `note:${id}`}]]` : null;
    }
    default: {
      const body = node.content?.some((c) => c.type === "text" || isInline(c)) ? inline(node.content, ctx) : children(node, ctx);
      return body ? `<!-- ${node.type} -->
${body}` : `<!-- ${node.type} -->`;
    }
  }
}
function isInline(n) {
  return n.type === "noteLink" || n.type === "tag" || n.type === "hardBreak" || n.type === "mention";
}
function listItems(node, ctx, marker) {
  return (node.content ?? []).map((item, i) => {
    const m = marker(i, item);
    let body = children(item, ctx, "\n");
    if (item.type === "taskItem") {
      const dates = [];
      const scheduled = attr(item, "scheduled");
      const deadline = attr(item, "deadline");
      if (scheduled) dates.push(`\u23F3 ${scheduled}`);
      if (deadline) dates.push(`\u{1F4C5} ${deadline}`);
      if (dates.length > 0) {
        const nl = body.indexOf("\n");
        body = nl < 0 ? `${body} ${dates.join(" ")}` : `${body.slice(0, nl)} ${dates.join(" ")}${body.slice(nl)}`;
      }
    }
    return prefixLines(body, m, " ".repeat(m.length));
  }).join("\n");
}
function table(node, ctx) {
  const rows = (node.content ?? []).map(
    (row) => (row.content ?? []).map(
      (cell) => (cell.content ?? []).map((p) => p.type === "paragraph" ? inline(p.content, ctx) : block(p, ctx) ?? "").join("<br>").replace(/\|/g, "\\|").replace(/\n/g, "<br>")
    )
  );
  if (rows.length === 0) return "";
  const width = Math.max(...rows.map((r) => r.length), 1);
  const line = (cells) => `| ${Array.from({ length: width }, (_, i) => cells[i] ?? "").join(" | ")} |`;
  return [line(rows[0]), line(Array(width).fill("---")), ...rows.slice(1).map(line)].join("\n");
}
function plain(content) {
  return (content ?? []).map((n) => n.type === "text" ? n.text ?? "" : n.type === "hardBreak" ? "\n" : "").join("");
}
var MARK_ORDER = ["link", "bold", "italic", "strike", "underline", "highlight", "code"];
function markKey(m) {
  return m.type === "link" ? `link:${String(m.attrs?.href ?? "")}` : m.type;
}
function open(m) {
  switch (m.type) {
    case "bold":
      return "**";
    case "italic":
      return "*";
    case "strike":
      return "~~";
    case "underline":
      return "<u>";
    case "highlight":
      return "==";
    case "code":
      return "`";
    case "link":
      return "[";
    default:
      return "";
  }
}
function close(m) {
  switch (m.type) {
    case "underline":
      return "</u>";
    case "link":
      return `](${String(m.attrs?.href ?? "")})`;
    default:
      return open(m);
  }
}
function rendered(marks) {
  return (marks ?? []).filter((m) => MARK_ORDER.includes(m.type)).sort((a, b) => MARK_ORDER.indexOf(a.type) - MARK_ORDER.indexOf(b.type));
}
function inline(content, ctx) {
  let out = "";
  let stack = [];
  const closeTo = (depth) => {
    while (stack.length > depth) out += close(stack.pop());
  };
  for (const node of content ?? []) {
    const marks = node.type === "text" ? rendered(node.marks) : [];
    const keys = marks.map(markKey);
    let keep = 0;
    while (keep < stack.length && keys.includes(markKey(stack[keep]))) keep++;
    closeTo(keep);
    for (const m of marks) {
      if (!stack.some((s) => markKey(s) === markKey(m))) {
        out += open(m);
        stack = [...stack, m];
      }
    }
    const inCode = stack.some((m) => m.type === "code");
    switch (node.type) {
      case "text":
        out += inCode ? node.text ?? "" : escapeText(node.text ?? "");
        break;
      case "noteLink": {
        const id = attr(node, "noteId");
        const label = attr(node, "label") ?? (id ? ctx.opts.noteTitle?.(id) : null) ?? `note:${id ?? ""}`;
        out += `[[${label}]]`;
        break;
      }
      case "tag":
        out += `#${attr(node, "name") ?? ""}`;
        break;
      case "hardBreak":
        out += "\\\n";
        break;
      case "mention":
        out += String(attr(node, "label") ?? "");
        break;
    }
  }
  closeTo(0);
  return out;
}
function escapeText(text) {
  return text.replace(/([\\`*_[\]<>~=#])/g, "\\$1");
}
function escapeBlockStart(text) {
  return text.replace(
    /^(\s*)([-+]|\d+[.)])(\s)/gm,
    (_, s, m, sp) => `${s}${m.replace(/([-+.)])/, "\\$1")}${sp}`
  );
}

// src/document/move.ts
async function moveNoteToNotebook(options) {
  const { noteId, fromIndex, toIndex } = options;
  const meta = getNoteMeta(fromIndex, noteId);
  if (!meta) throw new Error(`note ${noteId} is not in the source notebook`);
  const moved = {
    ...meta,
    wrappedKey: rewrapNoteKey(
      meta.wrappedKey,
      options.fromNotebookKey,
      options.toNotebookKey,
      noteId
    ),
    folderId: options.toFolderId ?? null,
    // Nesting under another note does not survive a move to another notebook.
    parentNoteId: null,
    updatedAt: (options.now?.() ?? /* @__PURE__ */ new Date()).toISOString()
  };
  putNoteMeta(toIndex, moved);
  try {
    await options.patchDoc(noteId, options.toNotebookId);
  } catch (e) {
    deleteNoteMeta(toIndex, noteId);
    throw e;
  }
  deleteNoteMeta(fromIndex, noteId);
  return moved;
}
function indexNoteState(noteId, state) {
  const doc = new Y.Doc({ gc: true });
  try {
    Y.applyUpdate(doc, state);
    return {
      noteId,
      title: getTitle(doc),
      plainText: getPlainText(doc),
      tasks: extractTasks(doc, noteId),
      links: extractNoteLinks(doc, noteId),
      tags: extractTags(doc)
    };
  } finally {
    doc.destroy();
  }
}
function createNoteIndexer(options) {
  const delay = options.delayMs ?? 300;
  const batchSize = options.batchSize ?? 200;
  const dirty = /* @__PURE__ */ new Set();
  let timer = null;
  let running = Promise.resolve();
  const run = async () => {
    while (dirty.size > 0) {
      const ids = [...dirty].slice(0, batchSize);
      for (const id of ids) dirty.delete(id);
      const out = [];
      for (const id of ids) {
        const state = await options.loadState(id);
        if (state) out.push(indexNoteState(id, state));
      }
      if (out.length > 0) await options.onIndex(out);
      await new Promise((r) => setTimeout(r, 0));
    }
  };
  const flush = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    running = running.then(run, run);
    return running;
  };
  return {
    schedule(noteId) {
      dirty.add(noteId);
      timer ??= setTimeout(() => void flush(), delay);
    },
    /** Index everything scheduled now; resolves when done. */
    flush,
    get pending() {
      return dirty.size;
    }
  };
}

export { BlockOperationError, applyBlockOperations, blockOperationSchema, buildBacklinks, createNoteIndexer, extractNoteLinks, extractTags, extractTasks, findBlock, getBlock, getPlainText, getTitle, indexNoteState, inlineText, listTopLevelBlocks, moveNoteToNotebook, noteToMarkdown, pmDocToMarkdown, setTitle, tagAncestors, validateBlock, walkElements };
//# sourceMappingURL=chunk-D2DTAVMT.js.map
//# sourceMappingURL=chunk-D2DTAVMT.js.map